
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NavigationMenu from '../../components/feature/NavigationMenu';

interface Subscriber {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  fundingOption: string;
  fundingAmount: number;
  fundingName: string;
  signupDate: string;
  status: 'active' | 'unsubscribed';
}

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState<Subscriber[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Mock admin credentials (in production, this should be in a secure database)
  const adminCredentials = {
    username: 'admin',
    password: 'marinepad2024'
  };

  // Mock subscriber data with funding information
  const mockSubscribers: Subscriber[] = [
    {
      id: '1',
      email: 'sarah.johnson@email.com',
      firstName: 'Sarah',
      lastName: 'Johnson',
      country: 'United States',
      fundingOption: 'complete-care',
      fundingAmount: 65,
      fundingName: 'Complete Care Bundle ($65)',
      signupDate: '2024-01-15T10:30:00Z',
      status: 'active'
    },
    {
      id: '2',
      email: 'emma.wilson@email.com',
      firstName: 'Emma',
      lastName: 'Wilson',
      country: 'Canada',
      fundingOption: 'starter-pack',
      fundingAmount: 35,
      fundingName: 'Starter Pack ($35)',
      signupDate: '2024-01-14T14:22:00Z',
      status: 'active'
    },
    {
      id: '3',
      email: 'lisa.chen@email.com',
      firstName: 'Lisa',
      lastName: 'Chen',
      country: 'Australia',
      fundingOption: 'champion-supporter',
      fundingAmount: 120,
      fundingName: 'Champion Supporter ($120)',
      signupDate: '2024-01-13T09:15:00Z',
      status: 'active'
    },
    {
      id: '4',
      email: 'maria.garcia@email.com',
      firstName: 'Maria',
      lastName: 'Garcia',
      country: 'Spain',
      fundingOption: 'early-bird-single',
      fundingAmount: 15,
      fundingName: 'Early Bird - Single Pack ($15)',
      signupDate: '2024-01-12T16:45:00Z',
      status: 'active'
    },
    {
      id: '5',
      email: 'anna.mueller@email.com',
      firstName: 'Anna',
      lastName: 'Mueller',
      country: 'Germany',
      fundingOption: 'ocean-guardian',
      fundingAmount: 250,
      fundingName: 'Ocean Guardian ($250)',
      signupDate: '2024-01-11T11:20:00Z',
      status: 'active'
    },
    {
      id: '6',
      email: 'yuki.tanaka@email.com',
      firstName: 'Yuki',
      lastName: 'Tanaka',
      country: 'Japan',
      fundingOption: 'updates-only',
      fundingAmount: 0,
      fundingName: 'Just updates (no funding)',
      signupDate: '2024-01-10T13:30:00Z',
      status: 'active'
    }
  ];

  useEffect(() => {
    if (isAuthenticated) {
      loadSubscribers();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    filterSubscribers();
  }, [subscribers, searchTerm, filterStatus]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (loginData.username === adminCredentials.username && 
        loginData.password === adminCredentials.password) {
      setIsAuthenticated(true);
      localStorage.setItem('admin_authenticated', 'true');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const loadSubscribers = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setSubscribers(mockSubscribers);
      setIsLoading(false);
    }, 1000);
  };

  const filterSubscribers = () => {
    let filtered = subscribers;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(sub => sub.status === filterStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.country.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSubscribers(filtered);
  };

  const exportToCSV = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'Country', 'Funding Option', 'Funding Amount', 'Signup Date', 'Status'];
    const csvContent = [
      headers.join(','),
      ...filteredSubscribers.map(sub => [
        sub.email,
        sub.firstName,
        sub.lastName,
        sub.country,
        sub.fundingName,
        sub.fundingAmount,
        new Date(sub.signupDate).toLocaleDateString(),
        sub.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'marine-pad-subscribers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const calculateStats = () => {
    const totalSubscribers = subscribers.length;
    const activeSubscribers = subscribers.filter(sub => sub.status === 'active').length;
    const totalFundingAmount = subscribers.reduce((sum, sub) => sum + sub.fundingAmount, 0);
    const averageFunding = totalFundingAmount / subscribers.filter(sub => sub.fundingAmount > 0).length || 0;

    return {
      totalSubscribers,
      activeSubscribers,
      totalFundingAmount,
      averageFunding
    };
  };

  const stats = calculateStats();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Login</h1>
            <p className="text-gray-600">Access Marine Pad dashboard</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={loginData.username}
                onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                required
              />
            </div>

            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                required
              />
            </div>

            {loginError && (
              <div className="mb-4 text-red-600 text-sm flex items-center">
                <i className="ri-error-warning-line mr-2"></i>
                {loginError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-teal-600 text-white py-2 px-4 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              Login
            </button>
          </form>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              Demo credentials: admin / marinepad2024
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <header className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold" style={{ fontFamily: '"Pacifico", serif' }}>
              Marine Pad
            </Link>
            <NavigationMenu />
            <Link
              to="/"
              className="bg-white text-teal-600 px-6 py-2 rounded-full font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Kickstarter Campaign Dashboard</h1>
          <p className="text-gray-600">Manage subscribers and track funding interest</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-line text-2xl text-teal-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Subscribers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSubscribers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-check-line text-2xl text-green-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Active Subscribers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeSubscribers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Expected Funding</p>
                <p className="text-2xl font-bold text-gray-900">${stats.totalFundingAmount}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-bar-chart-line text-2xl text-purple-600"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Avg. Funding</p>
                <p className="text-2xl font-bold text-gray-900">${Math.round(stats.averageFunding)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by email, name, or country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-8"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </div>
            <button
              onClick={exportToCSV}
              className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line mr-2"></i>
              Export CSV
            </button>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Subscribers ({filteredSubscribers.length})
            </h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">Loading subscribers...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscriber
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Funding Option
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signup Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {subscriber.firstName} {subscriber.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{subscriber.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {subscriber.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {subscriber.fundingName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${subscriber.fundingAmount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(subscriber.signupDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          subscriber.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {subscriber.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
